import { Injectable, inject, } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FormBuilder, Validators } from '@angular/forms';

import {FLEET_TYPES} from 'src/app/validation/constants/fleet-type';

import { FleetChecklistApiService } from './fleet-checklist-api.service';
import { VehicleValidationManagerService } from './vehicle-validation-manager.service';

import { SelectableVehicle } from '../models/vehicle-validation';
import { FleetChecklistUpdate, FleetChecklistCreate, FleetStatus, FleetChecklistSummary, FleetChecklist, FleetChecklistType } from '../models/fleet-checklist';

import { FleetChecklistHelper } from '../helpers/fleet-checklist.helper';

@Injectable({
  providedIn: 'root'
})
export class FleetChecklistEditorService {

  // VARIABLES
  public readonly fleetChecklistTypeOptions = [
    { name: FLEET_TYPES[FleetChecklistType.INTERNO], value: FleetChecklistType.INTERNO },
    { name: FLEET_TYPES[FleetChecklistType.EXTERNO], value: FleetChecklistType.EXTERNO },
    { name: FLEET_TYPES[FleetChecklistType.DIAZ], value: FleetChecklistType.DIAZ },
    { name: FLEET_TYPES[FleetChecklistType.MOVILBUS], value: FleetChecklistType.MOVILBUS },
  ];

  private createModalOpenSubject = new BehaviorSubject<boolean>(false);
  createModalOpen$ = this.createModalOpenSubject.asObservable();

  private editModalOpenSubject = new BehaviorSubject<boolean>(false);
  editModalOpen$ = this.editModalOpenSubject.asObservable();


  private fleetChecklistIdToUpdate: string | null = null;

  private fb = inject(FormBuilder);
  form = this.fb.group({
    name: ['', Validators.required],
    validationDate: [new Date(), Validators.required],
    typeValidation: ['', Validators.required],
    vehicles: [[], Validators.required],
    tags: [[]],
    parameters: [{}]
  });

  private get vehiclesCtrl() {
    return this.form.get('vehicles') as any;
  }
  public get vehicles(): SelectableVehicle[] {
    return this.vehiclesCtrl.value || [];
  }

  constructor(
    private api: FleetChecklistApiService,
    private vehicleValidationManager: VehicleValidationManagerService
  ) { }

  loadFormDefaults(): void {
    this.form.patchValue({
      name: '',
      validationDate: new Date(),
      typeValidation: 'interno',
      tags: [],
      vehicles: [],
      parameters: {}
    });
  }
  loadForm(fleet: FleetChecklistSummary): void {

    this.form.patchValue({
      name: fleet.name,
      validationDate: fleet.parameters?.validationDate
      ? new Date(fleet.parameters.validationDate + 'T00:00:00')
      : new Date(),
      typeValidation: fleet.type,
      tags: fleet.tags || [],
      parameters: fleet.parameters || {}
    });

    let vehicles = this.vehicleValidationManager.getVehiclesByIds(fleet.vehiclesIds.map(Number));

    this.form.get('vehicles')?.setValue(vehicles || []);
  }

  openToCreate(summary?: FleetChecklistSummary) {
    this.resetForm();
    this.loadFormDefaults();
    if (summary) {
      this.loadForm(summary);
      const currentName = this.form.get('name')?.value || '';
      this.form.get('name')?.setValue(`${currentName} - Copia`);
      this.fleetChecklistIdToUpdate = null;
    }
    this.createModalOpenSubject.next(true);
  }
  openToEdit(summary?: FleetChecklistSummary) {
    this.resetForm();
    if (summary) {
      this.fleetChecklistIdToUpdate = summary.id;
      console.log("load form suamry",summary);
      this.loadForm(summary);
    } else {
      this.loadFormDefaults();
    }
    this.editModalOpenSubject.next(true);
  }

  // Vehicles
  addVehicle(vehicle: SelectableVehicle): void {
    if (this.existsVehicle(vehicle.id)) return;

    const updated = [...this.vehicles, vehicle];
    this.vehiclesCtrl.setValue(updated);
  }
  removeVehicle(vehicleId: number): void {
    const updated = this.vehicles.filter(v => v.id != vehicleId);
    this.vehiclesCtrl.setValue(updated);
  }
  removeAllVehicles(): void {
    this.vehiclesCtrl.setValue([]);
  }
  existsVehicle(vehicleId: number): boolean {
    return this.vehicles.some(v => v.id === vehicleId);
  }

  resetForm(): void {
    this.fleetChecklistIdToUpdate = null;
    this.form.reset();
  }

  async onCreate(): Promise<FleetChecklist | null> {
    if (!this.isFormValid()) {
      this.form.markAllAsTouched();
      return null;
    }

    const typeValidation = this.form.get('typeValidation')?.value || FleetChecklistType.INTERNO;
    let fields = FleetChecklistHelper.getFieldsDynamicByType(typeValidation);
    const rawDate: Date = this.form.get('validationDate')?.value;
    const formattedDate = rawDate
      ? new Date(rawDate).toLocaleDateString('en-CA', {
          timeZone: 'America/Lima'
        })
      : null;
    let fleetType = FleetChecklistHelper.getFleetType(typeValidation);
    let tags = [fleetType];

    const newChecklist: FleetChecklistCreate = {
      name: this.form.get('name')?.value,
      status: FleetStatus.IN_PROGRESS,
      type: typeValidation,
      vehiclesIds: (this.form.get('vehicles')?.value || []).map((v: SelectableVehicle) => v.id),
      tags,
      fields,
      parameters: {
        validationDate: formattedDate || ''
      }
    };

    let res = await this.api.createFleetChecklist(newChecklist);
    if (!res) {
      return null
    }
    return res
  }

  async onEdit(): Promise<boolean | null> {
    if (!this.isFormValid()) {
      this.form.markAllAsTouched();
      return null;
    }

    const rawDate: Date = this.form.get('validationDate')?.value;
    const formattedDate = rawDate
      ? new Date(rawDate).toLocaleDateString('en-CA', {
          timeZone: 'America/Lima'
        })
      : null;
    const currentParameters = this.form.get('parameters')?.value || {};
    const updateChecklist: FleetChecklistUpdate = {
      id : this.fleetChecklistIdToUpdate || '',
      name: this.form.get('name')?.value,
      vehiclesIds: (this.form.get('vehicles')?.value || []).map((v: SelectableVehicle) => v.id.toString()),
      parameters: {
        ...currentParameters,
        validationDate: formattedDate || ''
      }
    };

    let updated = await this.api.updateFleetChecklist(updateChecklist);

    if (updated) {
      return updated
    }

    return null
  }
  isFormValid(): boolean {
    return this.form.valid;
  }
  // Modals
  closeModals() {
    this.createModalOpenSubject.next(false);
    this.editModalOpenSubject.next(false);
  }

}
